// **React Imports
import { memo, useEffect, useMemo, useState } from 'react'

// **MUI Imports
import { Box, Typography } from '@mui/material'

// **Third Party Imports
import { useSelector } from 'react-redux'

// **Component Imports
import MarkdownRenderer from '../markdown'

// **Utils
import CustomAvatar from '../../components/avatar'
import { getInitials } from '../../utils/utils'

// **Assets Imports
import scoutyImg from '../../assets/images/scouty.svg'
import { CHAT_BOT_ACTION_TYPES } from '../../utils/constant'
import CandidateInviteMessage from './CandidateInviteMessage'
import CandidateListMessage from './CandidateListMessage'
import CreditUsageEvent from './CreditUsageEvent'
import ChatPublicShareableLink from './ChatPublicShareableLink'
import ViewAITokenUsageMessage from './ViewAITokenUsageMessage'

const candidateListAction = [
  CHAT_BOT_ACTION_TYPES.CANDIDATE_SEARCH,
  CHAT_BOT_ACTION_TYPES.RESEND_INVITE,
  CHAT_BOT_ACTION_TYPES.CANDIDATE_RESUME,
  CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY,
  CHAT_BOT_ACTION_TYPES.UPDATE_MEDIA_CONFIG,
  CHAT_BOT_ACTION_TYPES.RE_ENABLE_ATTEMPT,
  CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY_NAVIGATE,
  CHAT_BOT_ACTION_TYPES.SCHEDULE_INTERVIEW,
  CHAT_BOT_ACTION_TYPES.RE_SCHEDULE_INTERVIEW,
  CHAT_BOT_ACTION_TYPES.REJECT_CANDIDATE,
  CHAT_BOT_ACTION_TYPES.MOVE_TO_NEXT_STAGE,
  CHAT_BOT_ACTION_TYPES.CANDIDATE_STATUS
]

const ChatMessage = ({
  message,
  onTyping,
  isLoading,
  onTypingEnd,
  isLastMessage,
  onTypingStart,
  handleReEnableClick,
  onSelectCandidateClick,
  onAwaitingAprrovalClick,
  handleInterviewClick
}) => {
  // State
  const [isTyping, setIsTyping] = useState(false)
  const [displayedText, setDisplayedText] = useState('')
  const [prevStatus, setPrevStatus] = useState(message.status)

  // Redux State
  const userData = useSelector((state) => state.auth.userData)
  const profile = useSelector((state) => state.auth.profile)

  // Variable
  const isUser = useMemo(() => message.role === 'user', [message?.role])

  const isThinking = useMemo(
    () => message.role === 'assistant' && isLoading,
    [message?.role, isLoading]
  )
  const fullText = useMemo(
    () => message?.messageText || '',
    [message?.messageText]
  )

  const assisstantMessageRenderer = useMemo(() => {
    if (message.role === 'assistant' && !isLoading) {
      if (message?.actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_INVITE) {
        return (
          <CandidateInviteMessage
            message={message}
            isLastMessage={isLastMessage}
            onAwaitingAprrovalClick={onAwaitingAprrovalClick}
          />
        )
      } else if (candidateListAction.includes(message?.actionType)) {
        return (
          <CandidateListMessage
            isLastMessage={isLastMessage}
            contestId={message?.confirmation?.contestId}
            navigateToProfile={message?.confirmation?.navigate || false}
            candidates={message?.confirmation?.data || []}
            actionType={message?.actionType}
            onSelectCandidateClick={(candidate) =>
              onSelectCandidateClick(message, candidate)
            }
            handleReEnableClick={handleReEnableClick}
            handleInterviewClick={(data) => handleInterviewClick(data, message)}
          />
        )
      } else if (message?.actionType === CHAT_BOT_ACTION_TYPES.TOTAL_CREDITS) {
        return <CreditUsageEvent data={message?.confirmation?.data} />
      } else if (
        message?.actionType ===
        CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY_PUBLIC_LINK
      ) {
        return <ChatPublicShareableLink data={message?.confirmation?.data} />
      } else if (
        message?.actionType === CHAT_BOT_ACTION_TYPES.VIEW_AI_TOKEN_USAGE
      ) {
        return <ViewAITokenUsageMessage data={message?.confirmation?.data} />
      } else if (message?.messageType === 'TEXT') {
        return (
          <Box className="chatbot-message__markdown">
            <MarkdownRenderer value={displayedText} />
          </Box>
        )
      }
    } else {
      return <></>
    }
  }, [
    message,
    isLoading,
    isLastMessage,
    displayedText,
    handleReEnableClick,
    onSelectCandidateClick,
    onAwaitingAprrovalClick,
    handleInterviewClick
  ])

  //useEffect
  useEffect(() => {
    setDisplayedText(fullText)
  }, [fullText])

  useEffect(() => {
    if (
      !isUser &&
      prevStatus === 'processing' &&
      message.status === 'success'
    ) {
      setDisplayedText('')
      setIsTyping(true)
      if (onTypingStart) {
        onTypingStart()
      }
    } else if (message.status !== 'processing' && !isTyping) {
      setDisplayedText(fullText)
    }
    setPrevStatus(message.status)
  }, [message.status, prevStatus, fullText, isTyping, isUser, onTypingStart])

  useEffect(() => {
    if (isTyping) {
      const tokens = fullText.split(/(\s+)/)
      let currentIndex = 0
      const interval = setInterval(() => {
        if (currentIndex < tokens.length) {
          setDisplayedText(tokens.slice(0, currentIndex + 1).join(''))
          currentIndex++
          if (onTyping) onTyping()
        } else {
          setIsTyping(false)
          clearInterval(interval)
          if (onTypingEnd) {
            onTypingEnd()
          }
        }
      }, 30)

      return () => clearInterval(interval)
    }
  }, [isTyping, fullText, onTyping, onTypingEnd])

  return (
    <Box
      className={`chatbot-message ${isUser ? 'chatbot-message--user' : 'chatbot-message--assistant'}`}
      sx={{ paddingBottom: isLastMessage ? '20px' : 0 }}
    >
      {!isUser && (
        <img
          src={scoutyImg}
          alt="Scouty AI"
          className="chatbot-message__avatar chatbot-message__avatar--ai-img"
          style={{ width: 24, height: 24, objectFit: 'contain' }}
        />
      )}
      <Box
        className={`chatbot-message__bubble ${isUser ? 'chatbot-message__bubble--user' : 'chatbot-message__bubble--assistant'}`}
      >
        {isThinking ? (
          <Box className="chatbot-thinking">
            <Box className="chatbot-thinking__dots">
              <span className="chatbot-thinking__dot" />
              <span className="chatbot-thinking__dot" />
              <span className="chatbot-thinking__dot" />
            </Box>
            <Typography variant="caption" className="chatbot-thinking__text">
              Thinking...
            </Typography>
          </Box>
        ) : isUser ? (
          <Typography
            variant="body2"
            className="chatbot-message__text"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {displayedText}
          </Typography>
        ) : (
          assisstantMessageRenderer
        )}
      </Box>
      {isUser &&
        (profile?.profilePicUri ? (
          <Box className="chatbot-message__avatar chatbot-message__avatar--user user-profile-picture-container">
            <img
              src={profile?.profilePicUri}
              alt="User profile"
              className="user-profile-picture"
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '1px solid var(--divider)'
              }}
            />
          </Box>
        ) : (
          <CustomAvatar
            className="chatbot-message__avatar chatbot-message__avatar--user"
            alt={userData?.name || ''}
            src="/"
            sx={{
              background: 'var(--primary-main)',
              color: 'var(--primary-contrastText)',
              fontSize: '11px',
              fontWeight: 600
            }}
          >
            {getInitials(userData?.name || '')}
          </CustomAvatar>
        ))}
    </Box>
  )
}

export default memo(ChatMessage)
